import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// The logo field (docs/07 "Logo upload states", T62).
//
// The file goes straight to storage before the form is submitted, so a
// validation error on another field never loses it. That is also why this is a
// component rather than a plain file input: an upload that happens out of band
// has states a file input has no way to show — in progress, done, cancelled,
// and four distinct ways to fail.
//
// Two rules that decide what it does:
//
//   **Reject in the browser what the server would reject anyway.** A 12 MB
//   photograph sent over a venue's wifi so it can be refused on arrival is two
//   minutes of somebody's morning. Size and type are checked before a single
//   byte leaves. The server checks them again — the browser is a convenience,
//   never the guard (docs/03).
//
//   **The logo is OPTIONAL, so an upload in progress never blocks the form.**
//   Continue stays live; only the submit on step 4 waits, and it says why.
export default class extends Controller {
  static targets = [
    "input", "signedId", "hint", "uploading", "filename", "progress",
    "done", "preview", "error", "retry"
  ]

  static values = {
    url: String,
    maxBytes: { type: Number, default: 5242880 },
    accept: { type: String, default: "image/png,image/jpeg,image/webp" }
  }

  // docs/07's five messages, word for word. They are here rather than in the
  // markup because four of the five are only reachable from JavaScript, and a
  // message nobody can find in the source is a message nobody reviews.
  static MESSAGES = {
    tooLarge: (size) => `${size} is over the 5 MB limit. Export a smaller PNG or JPG`,
    wrongType: "SVG and PDF aren't accepted. Upload PNG, JPG or WebP",
    interrupted: "Upload interrupted.",
    rateLimited: "Too many uploads. Please wait a few minutes",
    rejected: "This file isn't a valid image. Try another file"
  }

  connect() {
    this.showState(this.hasSignedIdTarget && this.signedIdTarget.value ? "done" : "before")
  }

  select() {
    const file = this.inputTarget.files[0]
    if (!file) return

    // Cleared as soon as it is read, so choosing the SAME file again fires a
    // change and re-uploads. Without this, "Replace" with the same file looks
    // like a page that has stopped responding.
    this.inputTarget.value = ""
    this.clearError()
    if (!this.acceptable(file)) return

    this.lastFile = file
    this.upload(file)
  }

  acceptable(file) {
    if (file.size > this.maxBytesValue) {
      this.fail(this.constructor.MESSAGES.tooLarge(this.humanSize(file.size)))
      return false
    }

    if (!this.acceptValue.split(",").includes(file.type)) {
      this.fail(this.constructor.MESSAGES.wrongType)
      return false
    }

    return true
  }

  upload(file) {
    this.filenameTarget.textContent = file.name
    this.setProgress(0)
    this.showState("uploading")
    this.dispatch("busy")

    this.upload_ = new DirectUpload(file, this.urlValue, {
      directUploadWillStoreFileWithXHR: (request) => {
        this.request = request
        request.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) this.setProgress((event.loaded / event.total) * 100)
        })
      }
    })

    this.upload_.create((error, blob) => {
      this.dispatch("idle")
      if (this.cancelled) return

      if (error) return this.fail(this.messageFor(error))

      this.setSignedId(blob.signed_id)
      this.previewTarget.src = URL.createObjectURL(file)
      this.showState("done")
    })
  }

  // Active Storage's DirectUpload reports a failed blob creation as a plain
  // STRING — `Error creating Blob for "logo.png". Status: 429` — and a failed
  // storage PUT as an Error object. The status is the only signal either one
  // carries, so it is read back out of the text; the alternative is
  // reimplementing the whole upload to keep hold of the response.
  //
  // Reading `error.message` alone gave `undefined` on the string case, so
  // every refusal read as "interrupted" and the rate limit never showed.
  messageFor(error) {
    const text = typeof error === "string" ? error : String(error?.message ?? error)
    const status = Number(text.match(/Status: (\d+)/)?.[1])

    if (status === 429) return this.constructor.MESSAGES.rateLimited
    if (status === 422) return this.constructor.MESSAGES.rejected

    return this.constructor.MESSAGES.interrupted
  }

  cancel() {
    this.cancelled = true
    this.request?.abort()
    this.dispatch("idle")
    this.reset()
  }

  retry() {
    if (!this.lastFile) return this.replace()

    this.cancelled = false
    this.clearError()
    this.upload(this.lastFile)
  }

  // Opens the file chooser rather than clearing first: somebody who changes
  // their mind mid-chooser should still have the logo they had.
  replace() {
    this.inputTarget.click()
  }

  // Setting `.value` from script fires nothing, so anything watching the form
  // — the prepared-details draft, for one — would never see the logo arrive.
  // The event is dispatched by hand, which is what the browser would have done
  // if a person had typed it.
  setSignedId(value) {
    this.signedIdTarget.value = value
    this.signedIdTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.signedIdTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  remove() {
    this.setSignedId("")
    this.previewTarget.removeAttribute("src")
    this.reset()
  }

  reset() {
    this.cancelled = false
    this.lastFile = null
    this.inputTarget.value = ""
    this.showState(this.signedIdTarget.value ? "done" : "before")
  }

  // --- What is on screen ----------------------------------------------------

  showState(state) {
    this.hintTarget.hidden = state !== "before"
    this.inputTarget.hidden = state !== "before"
    this.uploadingTarget.hidden = state !== "uploading"
    this.doneTarget.hidden = state !== "done"
  }

  setProgress(percent) {
    this.progressTarget.style.width = `${percent}%`
    this.progressTarget.parentElement?.setAttribute("aria-valuenow", String(Math.round(percent)))
  }

  // The error sits under the field and everything else is kept (docs/07): a
  // rejected file is not a reason to throw away the form.
  fail(message) {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
    this.retryTarget.hidden = message !== this.constructor.MESSAGES.interrupted
    this.inputTarget.value = ""
    this.showState(this.signedIdTarget.value ? "done" : "before")
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
    this.retryTarget.hidden = true
  }

  // "12.4 MB", the way the message quotes it back.
  humanSize(bytes) {
    const mb = bytes / 1048576
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`
  }
}
